import {
  ArrowLeftIcon,
  FloppyDiskIcon,
  PlusIcon,
  ReadCvLogoIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@reactive-resume/ui";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router";

import {
  useAdminUser,
  useDeleteAdminResume,
  useDeleteAdminUser,
  useDepartments,
  useUpdateAdminUser,
} from "@/client/services/admin";

import { CreateResumeDialog } from "../../_dialogs/create-resume";

export const AdminUserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user, loading } = useAdminUser(id!);
  const { departments } = useDepartments();
  const updateUser = useUpdateAdminUser();
  const deleteResume = useDeleteAdminResume();
  const deleteUser = useDeleteAdminUser();

  const [role, setRole] = useState<string>("WORKER");
  const [jobTitle, setJobTitle] = useState<string>("");
  const [department, setDepartment] = useState<string>("");
  const [skills, setSkills] = useState<string>("");
  const [hasChanges, setHasChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [createResumeOpen, setCreateResumeOpen] = useState(false);
  const [deleteResumeId, setDeleteResumeId] = useState<string | null>(null);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const navigate = useNavigate();

  const handleDeleteResume = () => {
    if (deleteResumeId) {
      deleteResume.mutate(deleteResumeId);
      setDeleteResumeId(null);
    }
  };

  const handleDeleteUser = () => {
    if (!id) return;
    deleteUser.mutate(id, {
      onSuccess: () => {
        navigate("/dashboard/admin");
      },
    });
    setDeleteUserOpen(false);
  };

  // Initialize form when user data loads
  if (user && !initialized) {
    setRole(user.role || "WORKER");
    setJobTitle(user.jobTitle ?? "");
    setDepartment(user.department ?? "");
    setSkills(user.skills?.join(", ") ?? "");
    setInitialized(true);
  }

  const handleSave = () => {
    if (!id) return;

    updateUser.mutate({
      id,
      data: {
        role,
        jobTitle: jobTitle || undefined,
        department: department || undefined,
        skills: skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      },
    });
    setHasChanges(false);
  };

  const handleChange = <T extends string>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center">
        <p className="text-muted-foreground">User not found</p>
        <Button asChild className="mt-4">
          <Link to="/dashboard/admin">Back to Admin</Link>
        </Button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {user.name} - Admin - Bloomteq Resume
        </title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button asChild variant="ghost" size="icon">
              <Link to="/dashboard/admin">
                <ArrowLeftIcon />
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{user.name}</h1>
              <p className="text-muted-foreground">{user.email}</p>
              <p className="text-sm text-muted-foreground">@{user.username}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10"
            onClick={() => setDeleteUserOpen(true)}
          >
            <TrashIcon className="mr-2" />
            Delete User
          </Button>
        </div>

        {/* User Details Card */}
        <Card>
          <CardHeader>
            <CardTitle>User Details</CardTitle>
            <CardDescription>View and edit user information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={role} onValueChange={handleChange(setRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WORKER">Employee</SelectItem>
                    <SelectItem value="MANAGER">Manager</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input
                  value={jobTitle}
                  onChange={(e) => handleChange(setJobTitle)(e.target.value)}
                  placeholder="e.g. Software Engineer"
                />
              </div>

              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={department || "none"} onValueChange={(v) => handleChange(setDepartment)(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments
                      ?.filter((dept) => dept && dept.trim() !== "")
                      .map((dept) => (
                        <SelectItem key={dept} value={dept}>
                          {dept}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Skills</Label>
                <Input
                  value={skills}
                  onChange={(e) => handleChange(setSkills)(e.target.value)}
                  placeholder="e.g. React, TypeScript, Node.js"
                />
                <p className="text-xs text-muted-foreground">Comma-separated list of skills</p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={!hasChanges || updateUser.isPending}>
                <FloppyDiskIcon className="mr-2" />
                {updateUser.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* User's Resumes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ReadCvLogoIcon />
                Resumes
              </CardTitle>
              <CardDescription>
                All resumes for this employee ({user.resumes?.length ?? 0})
              </CardDescription>
            </div>
            <Button onClick={() => setCreateResumeOpen(true)}>
              <PlusIcon className="mr-2 size-4" />
              Create Resume
            </Button>
          </CardHeader>
          <CardContent>
            {user.resumes && user.resumes.length > 0 ? (
              <div className="space-y-2">
                {user.resumes.map((resume) => (
                  <div
                    key={resume.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{resume.title}</span>
                        <Badge variant={resume.visibility === "public" ? "primary" : "secondary"}>
                          {resume.visibility === "public" ? "Public" : "Private"}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Updated: {new Date(resume.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/builder/${resume.id}`}>Edit</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteResumeId(resume.id)}
                      >
                        <TrashIcon />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-muted-foreground">No resumes yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateResumeDialog
        open={createResumeOpen}
        onOpenChange={setCreateResumeOpen}
        userId={id!}
        userName={user.name}
      />

      <AlertDialog open={!!deleteResumeId} onOpenChange={(open) => !open && setDeleteResumeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Resume</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this resume? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteResume}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {user.name}? This will permanently delete the user and all their resumes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
